import { useEffect, useState } from "react";
import Footer from "../../SharedModule/components/Footer";
import { Spinner } from "../../SharedModule/components/Spinner";
import { useDispatch, useSelector } from "react-redux";
import { setActiveTab } from "../../SharedModule/redux/reducers/uiShared.reducer";
import {
  CLIENTS_ADMIN_ACCESS,
  CLIENTS_ALL_ROLES,
  ROUTE_CLIENTS,
  ROUTE_CLIENTS_EDIT,
  ROUTE_CLIENTS_NEW_CLIENT,
} from "../../SharedModule/utils/constants";
import { useClients } from "../hooks/useClients";
import { verifyModuleAccess } from "../../SharedModule/utils/accessUtils";
import { ForbiddenPage } from "../../SharedModule/pages/ForbiddenPage";
import { Select } from "../../SharedModule/components/Select";
import { NoContent } from "../../SharedModule/components/NoContent";
import image from "./../../styles/legacy/404.png";
import { Client } from "../types/clients-module-types";
import {
  changeOrder,
  resetFilters,
  SelectOrderCriteria,
  setActualClientForHeaderInfo,
  setFilterOwnersSelected,
  setFilterText,
  setShowClients,
} from "../redux/reducers/clientsList.reducer";
import { useNavigate } from "react-router";
import { TbEdit } from "react-icons/tb";
import { checkClientCreatedByCostCenter } from "../utils/clientsUtils";
import { setIsClientDetailInfoLoaded } from "../redux/reducers/clientDetails.reducer";
import { setIsBillingInfoLoaded } from "../redux/reducers/clientBillingInfo.reducer";
import { resetScrollY } from "../../SharedModule/utils/pagesUtils";

export const ClientsListPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const sortBy = useSelector(SelectOrderCriteria);

  useEffect(() => {
    document.title = "The Hub - Clients";

    dispatch(
      changeOrder({
        orderBy: "companyName",
        dataType: "string",
        criteria: "asc",
      })
    );

    dispatch(setActiveTab(ROUTE_CLIENTS));

    let posY = localStorage.getItem("clientScrollY");
    if (posY)
      setTimeout(() => {
        window.scrollTo({
          top: Number(posY),
          behavior: "smooth",
        });
      }, 300);

    return () => {
      resetScrollY(globalThis.location.pathname);
      if (!globalThis.location.pathname.includes(ROUTE_CLIENTS)) {
        dispatch(resetFilters());
      }
    };
  }, []);

  const {
    clients,
    clientsOwners,
    rolesLoaded,
    isClientsLoading,
    isShowingClients,
    filterText,
    filterOwnersSelected,
    userRolesList,
  } = useClients();

  let clientsOwnersToShow = [];
  if (clientsOwners.length > 0) {
    clientsOwnersToShow = clientsOwners.toSorted((a, b) =>
      a.label.localeCompare(b.label)
    );
  }

  const [searchText, setSearchText] = useState(filterText);
  const [seeClients, setSeeClients] = useState(isShowingClients);
  const [filterOwner, setFilterOwner] = useState(filterOwnersSelected);
  const [ownerIdList, setOwnerIdList] = useState<string[]>(
    filterOwnersSelected.map((item) => item.value)
  );

  let clientsListToShow: Array<Client> = clients
    .filter((client) => {
      return isShowingClients ? client.isActive : !client.isActive;
    })
    .filter((client) => {
      const lowerSearch = searchText.toLocaleLowerCase();
      return (
        client.companyName.toLocaleLowerCase().includes(lowerSearch) ||
        client.companyAliasName?.toLocaleLowerCase().includes(lowerSearch)
      );
    })
    .filter((client) => {
      if (ownerIdList.length > 0) {
        return ownerIdList.includes(client.companyOwnerUserId);
      } else {
        return client;
      }
    });

  const search = (value: string) => {
    setSearchText(value);
    dispatch(setFilterText(value));
  };

  const onToggleButton = (state: boolean) => {
    setSeeClients(state);
    dispatch(setShowClients(!seeClients));
  };

  const filterByDropdown = (owner) => {
    setFilterOwner(owner);
    dispatch(setFilterOwnersSelected(owner));
    setOwnerIdList(owner.map((item) => item.value));
  };

  const addClient = () => {
    navigate(ROUTE_CLIENTS_NEW_CLIENT);
  };

  const goToProfile = (client: Client) => {
    localStorage.setItem("clientScrollY", window.scrollY.toString());
    dispatch(setActualClientForHeaderInfo(client));
    navigate(`/clients/${client.id}/details`);
  };

  const goToEditProfile = (event: any, client: Client) => {
    event.stopPropagation();
    dispatch(setIsClientDetailInfoLoaded(false));
    dispatch(setIsBillingInfoLoaded(false));
    navigate(ROUTE_CLIENTS_EDIT.replace(":id", client.id.toString()));
  };

  return (
    <>
      {(!rolesLoaded || isClientsLoading) && (
        <Spinner
          style={{
            marginLeft: "50%",
            marginTop: "5%",
          }}
        />
      )}

      {rolesLoaded &&
        !isClientsLoading &&
        !verifyModuleAccess(true, CLIENTS_ALL_ROLES, userRolesList) && (
          <ForbiddenPage />
        )}

      {rolesLoaded &&
        !isClientsLoading &&
        verifyModuleAccess(true, CLIENTS_ALL_ROLES, userRolesList) && (
          <div className="content">
            <div
              className="content-header"
              style={{ marginTop: "0", marginBottom: "1rem" }}
            >
              <h2>Clients</h2>
            </div>

            <div className="d-flex mb-3 justify-content-between">
              <div
                className="flex-column col-sm-5"
                style={{ marginTop: "3px" }}
              >
                <label htmlFor="clients-search" className="col-form-label">
                  Search
                </label>
                <input
                  id="clients-search"
                  type="text"
                  className="form-control form-control-sm common-input"
                  placeholder="Search by client name or Alias"
                  value={searchText}
                  onChange={(event) => search(event.target.value)}
                />
              </div>

              <div className="flex-column col-sm-3">
                <label htmlFor="client-owner" className="col-form-label">
                  Client Owner
                </label>
                <Select
                  inputId="client-owner"
                  isMulti
                  placeholder="All"
                  options={clientsOwnersToShow}
                  value={filterOwner}
                  onChange={(owner) => filterByDropdown(owner)}
                  isDisabled={isClientsLoading}
                />
              </div>

              <fieldset
                className="flex-column col-sm-2"
                style={{ marginTop: "3px" }}
              >
                <div className="col-form-label">Is Client?</div>
                <div className="btn-group w-100">
                  <button
                    type="button"
                    onClick={() => onToggleButton(true)}
                    className={`btn btn-group-custom ${
                      !seeClients ? "btn-light" : "btn-active"
                    }`}
                    disabled={seeClients || isClientsLoading}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => onToggleButton(false)}
                    className={`btn btn-group-custom ${
                      !seeClients ? "btn-inactive" : "btn-light"
                    }`}
                    disabled={!seeClients || isClientsLoading}
                  >
                    No
                  </button>
                </div>
              </fieldset>

              {verifyModuleAccess(
                true,
                CLIENTS_ADMIN_ACCESS,
                userRolesList
              ) && (
                <div className="flex-column" style={{ marginTop: "auto" }}>
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={() => addClient()}
                  >
                    Add Client
                  </button>
                </div>
              )}
            </div>

            {!isClientsLoading && clientsListToShow.length === 0 && (
              <div className="py-5">
                <NoContent
                  title="No Results"
                  text="Edit your filters to find the clients you are looking for."
                  image={image}
                  style={{ margin: "0 auto" }}
                />
              </div>
            )}

            {!isClientsLoading && clientsListToShow.length > 0 && (
              <table
                className="table grey-table-header-background mt-4 mb-7"
                style={{ width: "100%" }}
              >
                <thead className="align-middle sticky sticky-header">
                  <tr>
                    <th
                      className={`border-dark border-top fw-600 ps-5 column-55 pointer ${
                        sortBy.orderBy === "companyName"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      key="companyName"
                      id="companyName"
                      onClick={() =>
                        dispatch(
                          changeOrder({
                            orderBy: "companyName",
                            dataType: "string",
                          })
                        )
                      }
                    >
                      Name
                    </th>
                    <th
                      className={`border-dark border-top fw-600 ps-5 column-25 pointer ${
                        sortBy.orderBy === "companyOwnerName"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      key="companyOwnerName"
                      id="companyOwnerName"
                      onClick={() =>
                        dispatch(
                          changeOrder({
                            orderBy: "companyOwnerName",
                            dataType: "string",
                          })
                        )
                      }
                    >
                      Client Owner
                    </th>
                    <th className="border-dark border-top column-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {clientsListToShow.map((client: Client) => {
                    return (
                      <tr
                        key={client.id}
                        className="product-link"
                        onClick={() => goToProfile(client)}
                      >
                        <td className="ps-5 fw-500 td-shadow-effect">
                          {client.companyName}
                        </td>
                        <td className="ps-5 fw-500 td-shadow-effect">
                          {client.companyOwnerName}
                        </td>
                        <td className="td-shadow-effect">
                          {verifyModuleAccess(
                            true,
                            CLIENTS_ADMIN_ACCESS,
                            userRolesList
                          ) &&
                            !client.isExternal &&
                            client.isActive &&
                            !checkClientCreatedByCostCenter(
                              client.companyName
                            ) && (
                              <TbEdit
                                size={"20px"}
                                style={{ margin: "0 15px 5px 0" }}
                                className="purpleHover iconsRow"
                                onClick={(e) => goToEditProfile(e, client)}
                              />
                            )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            <Footer />
          </div>
        )}
    </>
  );
};
